import React, { useEffect, useMemo, useRef, useState } from 'react'

// =========================================================================
// TRADUCTOR VISUAL (Para los textos de Ganador en Pantalla)
// =========================================================================
const getVisualZoneName = (realZoneName) => {
    if (!realZoneName) return '';
    const normalized = realZoneName.toLowerCase().trim();

    const aliases = {
        'zona1': 'Zona Oriente',
        'zona 1': 'Zona Oriente',
        'zona oriente': 'Zona Oriente',
        'zona oriente (local)': 'Zona Oriente',
        'oriente': 'Zona Oriente',
        'noa local': 'Zona Oriente',
        'team noa local': 'Zona Oriente',
        'lucho': 'Zona Oriente',
        'team lucho': 'Zona Oriente',
        'player1': 'Zona Oriente',

        'zona2': 'Zona Poniente',
        'zona 2': 'Zona Poniente',
        'zona poniente': 'Zona Poniente',
        'zona poniente (visitante)': 'Zona Poniente',
        'poniente': 'Zona Poniente',
        'noa visitante': 'Zona Poniente',
        'team noa visitante': 'Zona Poniente',
        'tunita': 'Zona Poniente',
        'team tunita': 'Zona Poniente',
        'player2': 'Zona Poniente'
    };

    return aliases[normalized] || realZoneName;
};

const cleanVisualName = (name) => (name || '').replace(/\s*\([^)]*\)/g, '').trim();

const GoalScreenV2 = ({ currentMatchState }) => {
    const {
        phase = 'WAITING',
        elapsedMilliseconds = 0,
        totalDurationMilliseconds = 1,
        zonesProgress = [],
        winner = null
    } = currentMatchState || {};

    const [viewStage, setViewStage] = useState('RACING');
    const maxPositionsHistoryRef = useRef({});

    useEffect(() => {
        const currentPhase = phase.toUpperCase();

        if (currentPhase === 'WAITING' || currentPhase === 'IDLE') {
            maxPositionsHistoryRef.current = {};

        } else if (currentPhase === 'RUNNING') {
            setViewStage('RACING');

        } else if (currentPhase === 'FINISHED') {
            const isSingleWinner = winner && winner !== 'tie' && !winner.startsWith('tie_partial:');

            if (isSingleWinner) {
                setViewStage('KICK_ANIMATION');
                setTimeout(() => {
                    setViewStage('WINNER_TEXT');
                }, 2000);
            } else {
                setViewStage('WINNER_TEXT');
            }
        }
    }, [phase, winner]);

    // =========================================================================
    // AGRUPACIÓN INTELIGENTE: Consolida todas las zonas zombis en solo 2 carriles
    // =========================================================================
    const dynamicZonesToRender = useMemo(() => {
        if (!zonesProgress || zonesProgress.length === 0) return [];
        
        const grouped = {};
        
        zonesProgress.forEach(z => {
            const rawZone = z.zoneIdentifier || z.zone || 'unknown';
            const teamName = getVisualZoneName(rawZone);
            
            if (!grouped[teamName]) {
                grouped[teamName] = {
                    zoneIdentifier: teamName, // Usamos el equipo como ID único del carril
                    totalTaps: 0
                };
            }
            // Sumamos los taps de todos los que pertenezcan a este equipo
            grouped[teamName].totalTaps += (z.totalTaps || 0);
        });

        // Convertimos a array y ordenamos
        return Object.values(grouped).sort((a, b) => a.zoneIdentifier.localeCompare(b.zoneIdentifier));
    }, [zonesProgress]);

    const timeCompletedPercentage = useMemo(() => {
        if (phase.toUpperCase() === 'WAITING' || phase.toUpperCase() === 'IDLE') return 0;
        const percentage = (elapsedMilliseconds / totalDurationMilliseconds) * 100;
        return Math.min(100, Math.max(0, percentage));
    }, [elapsedMilliseconds, totalDurationMilliseconds, phase]);

    // 1. Calculamos al VERDADERO líder de la carrera usando nuestras zonas ya agrupadas
    const highestTapsOverall = useMemo(() => {
        if (!dynamicZonesToRender || dynamicZonesToRender.length === 0) return 0;
        return Math.max(...dynamicZonesToRender.map(z => z.totalTaps || 0));
    }, [dynamicZonesToRender]);

    const MAXIMUM_VISUAL_LAG_PERCENTAGE = 25; // Máximo rezago visual (25%)

    // =========================================================================
    // DICCIONARIO Y FUNCIÓN INTELIGENTE DE SKINS (Infalible por zona y carril)
    // =========================================================================
    const SKINS_CONFIG = {
        local: {
            avatar: '/img/games/runtogoal/noa-local-caricatura.png',
            spark: '/img/games/runtogoal/destellos-blancos.png'
        },
        visitante: {
            avatar: '/img/games/runtogoal/noa-visitante-caricatura.png',
            spark: '/img/games/runtogoal/destellos-azules.png'
        }
    };

    const getSkinForZone = (zoneIdentifier, laneIndex) => {
        const normalized = (zoneIdentifier || '').trim().toLowerCase();

        // 1. Detección directa por identificadores de Noa / Zonas / Oriente / Poniente
        if (
            normalized.includes('visitante') || 
            normalized.includes('poniente') ||
            normalized === 'zona2' || 
            normalized === 'zona 2' || 
            normalized === '2' || 
            normalized === 'player2' || 
            normalized === 'tunita'
        ) {
            return SKINS_CONFIG.visitante;
        }

        if (
            normalized.includes('local') || 
            normalized.includes('oriente') ||
            normalized === 'zona1' || 
            normalized === 'zona 1' || 
            normalized === '1' || 
            normalized === 'player1' || 
            normalized === 'lucho'
        ) {
            return SKINS_CONFIG.local;
        }

        // 2. Fallback inteligente por carril para que NUNCA se dupliquen
        if (laneIndex === 1) {
            return SKINS_CONFIG.visitante;
        }
        return SKINS_CONFIG.local;
    };

    // =========================================================================
    // HELPER PARA DETERMINAR IMAGEN DEL GANADOR
    // =========================================================================
    const getWinnerImage = (winnerRaw) => {
        if (!winnerRaw) return SKINS_CONFIG.local.avatar;
        
        const visualName = getVisualZoneName(winnerRaw).toUpperCase();
        
        if (visualName.includes('VISITANTE') || visualName.includes('PONIENTE') || visualName === 'TUNITA' || visualName === 'ZONA2' || visualName === '2' || visualName === 'PLAYER2') {
            return SKINS_CONFIG.visitante.avatar;
        }
        
        return SKINS_CONFIG.local.avatar;
    };

    const renderWinnerTextScreen = (winnerRaw) => {
        if (winnerRaw === 'tie') return '¡ES UN EMPATE!';
        if (winnerRaw.startsWith('tie_partial:')) {
            const tiedZones = winnerRaw.split(':')[1].split(' y ');
            const uniqueAliases = [...new Set(tiedZones.map(z => cleanVisualName(getVisualZoneName(z)).toUpperCase()))];
            return `¡HAN EMPATADO ${uniqueAliases.join(' Y ')}!`;
        }
        return `¡GOOOOOL DE ${cleanVisualName(getVisualZoneName(winnerRaw)).toUpperCase()}!`;
    };

    return (
        <>
            <div className='GoalScreenX__Sponsor' style={{ backgroundColor: '#fff', borderRadius: '8px', opacity: '0.8', padding: '6px', marginTop: '12px' }}>
                {/* <img src="/img/page/adsl-five-bicolor.png" className='GoalScreenX__Sponsor__Image' style={{ width: '100%', maxWidth: '350px' }} alt="Sponsor" /> */}
                <img src="/img/page/spins-five.png" className='GoalScreenX__Sponsor__Image' style={{ width: '100%', maxWidth: '350px' }} alt="Sponsor" />
            </div>

            <div className="goal">
                <div className="goal__pitch Goal__V2__Background"></div>

                <div className="goal__lanes" style={{ gridTemplateRows: `repeat(${dynamicZonesToRender.length}, 1fr)` }}>
                    {dynamicZonesToRender.map((zoneItem, index) => {
                        const zoneId = zoneItem.zoneIdentifier || `unknown-${index}`;
                        
                        // 2. MATEMÁTICA CORREGIDA (Avance suave basado en el líder real)
                        let finalPosition = timeCompletedPercentage;

                        if (highestTapsOverall > 0) {
                            const tapsDeficit = highestTapsOverall - (zoneItem.totalTaps || 0);
                            const lagRatio = tapsDeficit / highestTapsOverall;
                            // El líder va con el reloj (0 deficit). El que va perdiendo se retrasa visualmente.
                            finalPosition -= (lagRatio * MAXIMUM_VISUAL_LAG_PERCENTAGE);
                        }

                        // 3. EVITAR QUE CAMINEN PARA ATRÁS
                        if (phase.toUpperCase() === 'RUNNING') {
                            const previousMaxPosition = maxPositionsHistoryRef.current[zoneId] || 0;
                            if (finalPosition < previousMaxPosition) {
                                finalPosition = previousMaxPosition;
                            } else {
                                maxPositionsHistoryRef.current[zoneId] = finalPosition;
                            }
                        } else if (phase.toUpperCase() === 'WAITING' || phase.toUpperCase() === 'IDLE') {
                            finalPosition = 0;
                        }

                        finalPosition = Math.min(100, Math.max(0, finalPosition));
                        const skin = getSkinForZone(zoneId, index);

                        // 3. CORRECCIÓN DE PERSPECTIVA DE LA CANCHA
                        // Ajuste de Perspectiva: Mueve SOLO LA META. 
                        // El carril superior frena a 20vw del borde. El inferior frena a 14vw (para igualar la portería inclinada).
                        // Al estar en 0%, ambos arrancan desde la misma línea exacta.
                        const finishGap = 20 - (index * 6); // Tiro a gol
                        // const finishGap = 20 - (index * 5);  // Carrera Atleti

                        return (
                            <div key={zoneId} className="goal__lane" style={{ '--finish-gap': `${finishGap}vw` }}>
                                <div className="goal__runner" style={{ '--pct': finalPosition / 100 }}>
                                    <img className="goal__velocity" src={skin.spark} alt="destellos" />
                                    <img className="goal__avatar" src={skin.avatar} alt="Jugador" />
                                    {/* Agregar balón en tiro a gol */}
                                    <div className='goal__ball__container'>
                                        <img className="goal__ball__container__ball" src="/img/games/runtogoal/balon.png" alt="Balón" />
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {viewStage === 'KICK_ANIMATION' && (
                    <div className="goal-outro">
                        <img src="/img/games/runtogoal/balon-grande.png" className="goal-outro__ball" alt="Balón" /> {/* Tiro a gol */}
                        {/* <img src={getWinnerImage(winner)} className="goal-outro__ball" alt="Ganador" /> */}
                    </div>
                )}

                {viewStage === 'WINNER_TEXT' && (
                    <div className="goal__pov">
                        {winner ? (
                            <div className="goal__povGoool text-center">
                                {renderWinnerTextScreen(winner)}
                            </div>
                        ) : (
                            // <div className="goal__povGoool text-center">¡CARRERA FINALIZADA!</div>
                            <div className="goal__povGoool text-center">¡GOOOOOL!</div>
                        )}
                        <div className="goal__fw">
                            <div className="goal__fwBurst" />
                            <div className="goal__fwBurst goal__fwBurst--b2" />
                            <div className="goal__fwBurst goal__fwBurst--b3" />
                        </div>
                    </div>
                )}
            </div>

            <footer className='goal__footer'>
                <p className='text-center FooterCredits'>Hecho con ♥ por Interaktia | 2026</p>
            </footer>
        </>
    );
};

export { GoalScreenV2 };